import {
  ALLOWED_IMAGE_CONTENT_TYPES,
  type AllowedImageContentType,
  DESIGN_ASSET_UPLOAD_EXPIRED_MESSAGE,
  MAX_IMAGE_UPLOAD_BYTES,
} from '@repo/api-contracts';
import { useState } from 'react';
import { trpc } from '@/shared/api';

/** 정책 §5 — 가로·세로 각 20000px 이하(계약 facePhotoSize 상한과 같다). */
export const MAX_DESIGN_IMAGE_PX = 20000;

export interface UploadedDesignImage {
  readonly assetId: string;
  readonly widthPx: number;
  readonly heightPx: number;
  readonly url: string;
}

interface UploadOptions {
  readonly readSize?: (file: File) => Promise<{ width: number; height: number }>;
  readonly fetchImpl?: typeof fetch;
  /** 비회원 키(G6). 있으면 비회원 API(designs.guestAssets.*)로 그 키와 함께 올린다. 없거나 null 이면 회원 API. */
  readonly guestKey?: string | null;
}

export interface UploadDesignImage {
  readonly upload: (file: File) => Promise<UploadedDesignImage | null>;
  readonly isUploading: boolean;
  readonly error: string | null;
  readonly clearError: () => void;
}

const readBitmapSize = async (file: File): Promise<{ width: number; height: number }> => {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
};

const messageOf = (error: unknown): string => {
  if (typeof error !== 'object' || error === null || !('message' in error)) return '';
  const { message } = error;
  return typeof message === 'string' ? message : '';
};

/**
 * 고객 이미지 업로드. 형식·용량·픽셀은 먼저 거르고(T12), presign → PUT → confirm 순으로 올린다.
 * 회원은 designs.assets.*, 비회원은 비회원 키로 designs.guestAssets.*(G6) — 한도 초과(429) 등 서버 한글 문구는 그대로 보여 준다.
 */
export const useUploadDesignImage = (options: UploadOptions = {}): UploadDesignImage => {
  const readSize = options.readSize ?? readBitmapSize;
  const guestKey = options.guestKey ?? null;
  const presign = trpc.designs.assets.presignUpload.useMutation();
  const confirm = trpc.designs.assets.confirmUpload.useMutation();
  const guestPresign = trpc.designs.guestAssets.presignUpload.useMutation();
  const guestConfirm = trpc.designs.guestAssets.confirmUpload.useMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<UploadedDesignImage | null> => {
    if (!(ALLOWED_IMAGE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      setError('jpeg · png · webp 형식만 올릴 수 있습니다.');
      return null;
    }
    if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
      setError('이미지는 10MB 이하여야 합니다.');
      return null;
    }
    setIsUploading(true);
    setError(null);
    try {
      let size: { width: number; height: number };
      try {
        size = await readSize(file);
      } catch {
        setError('이미지를 읽을 수 없습니다. 손상되지 않은 PNG·JPG·WebP 파일인지 확인해 주세요.');
        return null;
      }
      if (size.width > MAX_DESIGN_IMAGE_PX || size.height > MAX_DESIGN_IMAGE_PX) {
        setError(`이미지는 가로·세로 각 ${MAX_DESIGN_IMAGE_PX}px 이하여야 합니다.`);
        return null;
      }
      const contentType = file.type as AllowedImageContentType;
      const target =
        guestKey === null
          ? await presign.mutateAsync({ contentType })
          : await guestPresign.mutateAsync({ guestKey, contentType });
      const put = options.fetchImpl ?? fetch;
      const response = await put(target.uploadUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!response.ok) {
        setError(`업로드에 실패했습니다 (HTTP ${response.status}). 다시 시도해 주세요.`);
        return null;
      }
      const asset =
        guestKey === null
          ? await confirm.mutateAsync({ assetId: target.assetId })
          : await guestConfirm.mutateAsync({ guestKey, assetId: target.assetId });
      return {
        assetId: asset.id,
        widthPx: asset.widthPx,
        heightPx: asset.heightPx,
        url: asset.url,
      };
    } catch (cause) {
      const message = messageOf(cause);
      if (message === DESIGN_ASSET_UPLOAD_EXPIRED_MESSAGE)
        setError('업로드 시간이 지났습니다. 이미지를 다시 올려 주세요.');
      else if (/[가-힣]/.test(message)) setError(message);
      else setError('업로드에 실패했습니다. 다시 시도해 주세요.');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { upload, isUploading, error, clearError: () => setError(null) };
};
